import Button from "@components/atoms/button";
import Header from "@components/molecules/header";
import Layout from "@components/molecules/layout";
import Transaction from "@components/molecules/transaction";
import { LoaderSnackbarWrapperActions } from "@components/organisms/LoaderSnackbarWrapper/LoaderSnackbarWrapper.slice";
import theme from "@config/theme";
import { Box, MenuItem, SelectChangeEvent } from "@mui/material";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { NumericFormat } from "react-number-format";
import { ConnectedProps, connect } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { gameDetailsActions } from "../game-details/gameDetails.slice";
import { BalanceProps, PaymentIntentProps } from "./transaction.type";
import { transactionsActions } from "./transactions.slice";
import {
  BalanceWrapper,
  Content,
  ContentHeader,
  Select,
  TitleWrapper,
  TransactionsWrapper,
} from "./transactions.styled";

type ReduxProps = ConnectedProps<typeof connector>;

const Transactions = ({
  userHasStripeAccount,
  getBalance,
  getPaymentsIntents,
  getTransactions,
  fetchAccountDashboardLink,
  fetchAccountOnboardingLink,
  createAccount,
  setAlert,
  createPayout,
  resetGame,
}: ReduxProps) => {
  const contentTitleRef = useRef<HTMLDivElement>();
  const navigate = useNavigate();
  const [limitIntents, setlimitIntents] = useState(10);
  const [totalIntents, setTotalIntents] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [txnType, setTxnType] = useState<string | null>(
    searchParams.get("txn_type") ? searchParams.get("txn_type") : "OUT"
  );

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    setTxnType(event.target.value as string);
  };

  const OutTxns = () => {
    const [paymentIntents, setPaymentIntents] = useState<PaymentIntentProps[]>(
      []
    );
    const fetchPaymentsIntents = async () => {
      const response = await getPaymentsIntents({
        limit: limitIntents,
        offset: 0,
      });
      if (response?.meta?.requestStatus === "fulfilled") {
        setPaymentIntents(response?.payload?.items);
        setTotalIntents(response?.payload?.count);
      }
    };

    const fetchPaymentsIntentsNext = async () => {
      const response = await getPaymentsIntents({
        limit: limitIntents,
        offset: 0,
      });
      if (response?.meta?.requestStatus === "fulfilled") {
        setPaymentIntents(response?.payload?.items);
        setTotalIntents(response?.payload?.count);
      }
      setlimitIntents((cur) => {
        return cur + 5;
      });
    };

    useEffect(() => {
      fetchPaymentsIntents();
    }, []);

    return (
      <Content>
        {paymentIntents && paymentIntents?.length > 0 ? (
          <div
            id="scrollableDiv"
            style={{
              height: "calc(100svh - 150px - 100px)",
              overflow: "auto",
            }}
          >
            <InfiniteScroll
              dataLength={totalIntents}
              next={fetchPaymentsIntentsNext}
              hasMore={!(paymentIntents.length === totalIntents)}
              loader={<h4>Loading...</h4>}
              height={
                paymentIntents.length < 10
                  ? "calc(100svh - 150px - 100px)"
                  : paymentIntents.length * 54
              }
              scrollableTarget="scrollableDiv"
              // endMessage={
              //   <p style={{ textAlign: "center" }}>
              //     <b>No more transactions!</b>
              //   </p>
              // }
            >
              <TransactionsWrapper>
                {paymentIntents.map((payment_intent: PaymentIntentProps) => (
                  <Transaction
                    title={payment_intent?.game_rsvp?.game?.title}
                    status={"out"}
                    amount={payment_intent?.amount / 100}
                    currency={payment_intent?.currency}
                    date={payment_intent?.created_at}
                    id={payment_intent?.id}
                  />
                ))}
              </TransactionsWrapper>
            </InfiniteScroll>
          </div>
        ) : (
          <Content>
            <Typography fontSize="16px">No payments yet.</Typography>
            <Typography fontSize="14px">
              Pay for a game in-app to start seeing your payments here.
            </Typography>
          </Content>
        )}
      </Content>
    );
  };

  const StripeConnectBalance = () => {
    const [balance, setBalance] = useState<BalanceProps>({
      available_balance: {
        gbp: -1,
      },
      pending_balance: {
        gbp: -1,
      },
    });

    const fetchBalance = async () => {
      const balance_response = await getBalance();
      if (balance_response?.meta?.requestStatus === "fulfilled") {
        setBalance(balance_response?.payload);
      }
    };

    const handlePayout = async () => {
      const res = await createPayout();
      if (res?.meta.requestStatus === "fulfilled") {
        navigate("/settings/transactions?txn_type=IN");
      }
    };

    useEffect(() => {
      fetchBalance();
    }, []);

    return (
      <Content>
        <Box sx={{ mt: 3, mb: 3 }}>
          <Divider flexItem orientation="horizontal" />
        </Box>
        <BalanceWrapper>
          <Grid container spacing={2} flexGrow={1} columns={2}>
            <Grid item style={{ width: "55%" }}>
              <Typography fontWeight={"Bold"}>Your Balance:</Typography>
              <Typography>
                <NumericFormat
                  value={(balance.pending_balance?.gbp / 100).toFixed(2)}
                  displayType={"text"}
                  thousandSeparator={true}
                  prefix={Object.keys(balance.pending_balance)[0].toUpperCase()}
                  renderText={(value) => <Typography>{value}</Typography>}
                />
              </Typography>
              <Typography>
                <NumericFormat
                  value={(balance.available_balance?.gbp / 100).toFixed(2)}
                  displayType={"text"}
                  thousandSeparator={true}
                  prefix={Object.keys(
                    balance.available_balance
                  )[0].toUpperCase()}
                  renderText={(value) => (
                    <Typography fontSize="10px">{value} available</Typography>
                  )}
                />
              </Typography>
            </Grid>
            <Grid item style={{ width: "45%" }}>
              <Button
                textColor={theme.palette.grey[100]}
                text="Pay out now"
                type="button"
                color="primary"
                onClick={() => {
                  handlePayout();
                }}
              />
            </Grid>
          </Grid>
        </BalanceWrapper>
        <Box sx={{ mt: 3, mb: 3 }}>
          <Divider flexItem orientation="horizontal" />
        </Box>
      </Content>
    );
  };

  const InTxns = () => {
    const [stripeAccount, setStripeAccount] = useState<any>();
    const [stripeUrl, setStripeUrl] = useState<string>("");

    const hasStripeConnectAccount = async () => {
      const response = await userHasStripeAccount();
      setStripeAccount(response?.payload);
      if (response?.payload?.details_submitted === true) {
        const response = await fetchAccountDashboardLink();
        if (response?.payload?.stripe_account_link) {
          setStripeUrl(response?.payload?.stripe_account_link);
        }
      } else if (response?.payload?.details_submitted === false) {
        const response = await fetchAccountOnboardingLink();
        if (response?.payload?.stripe_account_link) {
          setStripeUrl(response?.payload?.stripe_account_link);
        }
      }
    };

    const handleAccountCreation = async () => {
      const res = await createAccount();
      if (res?.meta.requestStatus === "fulfilled") {
        setAlert({
          alert: {
            isVisible: true,
            message: `Payments account created`,
            type: "success",
          },
        });
        navigate("/settings/transactions?txn_type=IN");
      }
    };

    useEffect(() => {
      hasStripeConnectAccount();
    }, []);

    if (stripeAccount?.details_submitted === true) {
      return (
        <Content>
          <StripeConnectBalance />
          <Button
            textColor={theme.palette.grey[100]}
            text="View Payments Dashboard"
            type="button"
            color="secondary"
            onClick={() => {
              window.open(stripeUrl, "_blank");
            }}
          />
        </Content>
      );
    } else if (stripeAccount?.details_submitted === false) {
      return (
        <Content>
          <Button
            iscta
            textColor={theme.palette.grey[100]}
            text="Finish onboarding"
            type="button"
            color="secondary"
            onClick={() => {
              window.open(stripeUrl, "_blank");
            }}
          />
        </Content>
      );
    } else {
      return (
        <Content>
          <Button
            iscta
            textColor={theme.palette.grey[100]}
            text="Create Payments Account"
            type="button"
            color="primary"
            onClick={handleAccountCreation}
          />
        </Content>
      );
    }
  };

  return (
    <Layout
      isLayout
      HeaderChildren={
        <Header
          title="Transactions"
          handleBack={() => {
            navigate("/settings");
            resetGame();
          }}
        />
      }
      CardChildren={<></>}
    >
      <Content>
        <ContentHeader ref={contentTitleRef}>
          <TitleWrapper>
            <Select
              sx={{
                fontFamily: "NotoSans-Regular",
                fontSize: "14px",
              }}
              labelId="demo-simple-select-label"
              id="txn-select"
              value={txnType}
              onChange={(e: SelectChangeEvent<unknown>) => {
                handleChange(e);
                setlimitIntents(4);
              }}
            >
              <MenuItem
                sx={{
                  minHeight: "0px !important",
                  minWidth: "100px !important",
                }}
                value={"IN"}
              >
                Organizer
              </MenuItem>
              <MenuItem sx={{ minHeight: "0px !important" }} value={"OUT"}>
                Player
              </MenuItem>
            </Select>
          </TitleWrapper>
          {txnType === "IN" ? <InTxns /> : <OutTxns />}
        </ContentHeader>
      </Content>
    </Layout>
  );
};

const mapDispatchToProps = {
  userHasStripeAccount: transactionsActions.userHasStripeAccount,
  getBalance: transactionsActions.getBalance,
  createPayout: transactionsActions.createPayout,
  getPaymentsIntents: transactionsActions.getPaymentsIntents,
  getTransactions: transactionsActions.getTransactions,
  fetchAccountDashboardLink: transactionsActions.fetchAccountDashboardLink,
  fetchAccountOnboardingLink: transactionsActions.fetchAccountOnboardingLink,
  createAccount: transactionsActions.createAccount,
  setAlert: LoaderSnackbarWrapperActions.setGlobalAppearances,
  resetGame: gameDetailsActions.reset,
};

const connector = connect(undefined, mapDispatchToProps);
const TransactionsRedux = connector(Transactions);

export { TransactionsRedux as Transactions };
